import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { exchangeOAuthCode } from './cognito';
import { useAuth } from './AuthContext';

export function CallbackScreen() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setAuthed } = useAuth();
  const [error, setError] = useState<string | null>(null);
  // Module-scoped ref guards against React 18 StrictMode double-mount in dev:
  // the first effect run consumes the PKCE verifier/state from sessionStorage,
  // and we MUST NOT let the second invocation see a missing verifier and
  // report a spurious error. The first call's promise is allowed to settle
  // even if its effect was "cancelled" by StrictMode.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const code = params.get('code');
    const state = params.get('state');
    const cognitoError = params.get('error');

    if (cognitoError) {
      setError(params.get('error_description') || cognitoError);
      return;
    }
    if (!code || !state) {
      setError('Missing OAuth response — try signing in again.');
      return;
    }

    exchangeOAuthCode(code, state)
      .then(() => {
        setAuthed(true);
        navigate('/', { replace: true });
      })
      .catch((err) => {
        setError((err as Error).message);
      });
  }, [params, navigate, setAuthed]);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
      flexDirection: 'column',
      gap: 16,
    }}>
      {error ? (
        <>
          <div className="rc-kicker">Could not sign in</div>
          <p style={{ font: '500 15px/1.4 var(--rc-sans)', color: 'var(--rc-ink-2)', margin: 0, maxWidth: 360, textAlign: 'center' }}>
            {error}
          </p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            style={{
              appearance: 'none', border: 0, cursor: 'pointer',
              padding: '10px 16px', borderRadius: 999,
              background: 'var(--rc-ink)', color: '#fff',
              font: '600 13px/1 var(--rc-sans)',
            }}>
            Back to sign in
          </button>
        </>
      ) : (
        <div className="rc-kicker">Signing you in…</div>
      )}
    </div>
  );
}

export function LogoutScreen() {
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/login', { replace: true });
  }, [navigate]);
  return null;
}
